import React, {useCallback, useId} from "react";
import CodeMirror from "@uiw/react-codemirror";
import {json} from "@codemirror/lang-json";
import {FieldError} from "./FieldError";
import {GolemErrorState} from "./useGolemErrors";

type GolemFieldProps = {
    errors: GolemErrorState;
    field: string;
    children: React.ReactNode;
}

export function GolemField({errors, field, children}: GolemFieldProps) {
    return (
        <div>
            {children}
            <FieldError error={errors.fieldError(field)}/>
        </div>
    );
}

const inputClass = (error: boolean) =>
    `h-10 w-full rounded-md px-4 bg-white shadow-md transition-all ${
        error ? "outline outline-2 outline-red -outline-offset-2" : "outline-none"
    } disabled:opacity-50 disabled:cursor-not-allowed`;

const textareaClass = (error: boolean) =>
    `w-full rounded-md px-4 py-1 bg-white shadow-md transition-all ${
        error ? "outline outline-2 outline-red -outline-offset-2" : "outline-none"
    }`;

type GolemTextProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    suggestions?: string[];
}

export function GolemText({label, value, onChange, errors, field, placeholder, disabled, required, suggestions}: GolemTextProps) {
    const error = errors.fieldError(field);
    const listID = useId();

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    return (
        <GolemField errors={errors} field={field}>
            {label != null && (
                <label className={error ? "text-red" : ""}>
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <input
                className={inputClass(!!error)}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                list={suggestions ? listID : undefined}
            />
            {suggestions && suggestions.length > 0 && (
                <datalist id={listID}>
                    {suggestions.map((s) => <option key={s} value={s}/>)}
                </datalist>
            )}
        </GolemField>
    );
}


type GolemTextAreaProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    rows?: number;
}

export function GolemTextArea({label, value, onChange, errors, field, placeholder, disabled, required, rows = 6}: GolemTextAreaProps) {
    const error = errors.fieldError(field);

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    return (
        <GolemField errors={errors} field={field}>
            {label != null && (
                <label className={`mb-1 ${error ? "text-red" : ""}`}>
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <textarea
                className={textareaClass(!!error)}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows}
            />
        </GolemField>
    );
}

type GolemJSONProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    required?: boolean;
    minHeight?: string;
}

export function GolemJSON({label, value, onChange, errors, field, required, minHeight = "120px"}: GolemJSONProps) {
    const error = errors.fieldError(field);

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    return (
        <GolemField errors={errors} field={field}>
            {label != null && (
                <label className={`mb-1 ${error ? "text-red" : ""}`}>
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <div className={`rounded-md overflow-hidden border ${error ? "border-red" : "border-light-light-grey"} max-h-64 overflow-y-auto`}>
                <CodeMirror
                    value={value}
                    extensions={[json()]}
                    onChange={handleChange}
                    minHeight={minHeight}
                    basicSetup={{lineNumbers: true, foldGutter: false}}
                />
            </div>
        </GolemField>
    );
}
